import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X, Maximize2, Minimize2 } from "lucide-react"
import { api } from "@/lib/api"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// Windows never close on a click outside (a stray click used to throw away everything typed); a Radix Select/Popover menu
// is portaled OUTSIDE the dialog DOM too, so this also keeps iOS Safari from closing the dialog when a menu item is tapped.

const sizeMemory = new Map();
let prefsLoaded = null;
const loadPrefs = () => {
  if (!prefsLoaded) prefsLoaded = api.get("/dialog-prefs").then(({ data }) => { Object.entries(data || {}).forEach(([k, v]) => sizeMemory.set(k, v)); }).catch(() => {});
  return prefsLoaded;
};
const saveTimers = new Map();
const persistSize = (key, s) => {
  sizeMemory.set(key, s);
  clearTimeout(saveTimers.get(key));
  saveTimers.set(key, setTimeout(() => { api.put(`/dialog-prefs/${encodeURIComponent(key)}`, s).catch(() => {}); }, 500));
};

const useDialogGeometry = (ref) => {
  const [size, setSize] = React.useState(null);
  const [maxed, setMaxed] = React.useState(false);
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const keyRef = React.useRef(null);
  const attach = React.useCallback((el) => {
    if (!el || keyRef.current) return;
    const key = (el.querySelector("h2")?.textContent || "dialog").trim().slice(0, 120);
    keyRef.current = key;
    const apply = () => { if (sizeMemory.has(key)) setSize(sizeMemory.get(key)); };
    apply(); loadPrefs().then(apply);
  }, []);
  const drag = (e, onMove) => {
    e.preventDefault(); e.stopPropagation();
    const start = { x: e.clientX, y: e.clientY };
    const move = (ev) => onMove(ev.clientX - start.x, ev.clientY - start.y);
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };
  const onResizeDown = (e) => {
    const el = ref.current; if (!el || maxed) return;
    const r = el.getBoundingClientRect();
    drag(e, (dx, dy) => {
      const s = { width: Math.max(320, Math.min(window.innerWidth - 24, r.width + dx * 2)), height: Math.max(200, Math.min(window.innerHeight - 24, r.height + dy * 2)) };
      setSize(s); if (keyRef.current) persistSize(keyRef.current, s);
    });
  };
  const onMoveDown = (e) => {
    if (maxed || e.button !== 0 || e.target.closest("button,input,select,textarea,a,[role=combobox]")) return;
    const base = offset;
    drag(e, (dx, dy) => setOffset({ x: base.x + dx, y: base.y + dy }));
  };
  const style = maxed
    ? { width: "calc(100vw - 16px)", height: "calc(100vh - 16px)", maxWidth: "none", maxHeight: "none", alignContent: "start", gridTemplateRows: "auto", transform: "translate(-50%, -50%)" }
    : {
      ...(size ? { width: size.width, height: size.height, maxWidth: "none", maxHeight: "none", alignContent: "start", gridTemplateRows: "auto" } : {}),
      ...((offset.x || offset.y) ? { transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))` } : {}),
    };
  return { style, maxed, toggleMax: () => setMaxed((m) => !m), onResizeDown, onMoveDown, attach };
};

const DialogContent = React.forwardRef(({ className, children, onPointerDownOutside, onInteractOutside, style, ...props }, ref) => {
  const innerRef = React.useRef(null);
  const geo = useDialogGeometry(innerRef);
  const setRefs = (node) => { innerRef.current = node; geo.attach(node); if (typeof ref === "function") ref(node); else if (ref) ref.current = node; };
  const btn = "absolute top-4 rounded-md opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none";
  return (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={setRefs}
      style={{ ...style, ...geo.style }}
      onPointerDownOutside={(e) => {
        onPointerDownOutside?.(e);
        e.preventDefault();   // data-entry windows only close via Close / Cancel / Save — never by a click outside
      }}
      onInteractOutside={(e) => {
        onInteractOutside?.(e);
        e.preventDefault();
      }}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg max-h-[92vh] overflow-y-auto translate-x-[-50%] translate-y-[-50%] gap-4 border border-border bg-card text-card-foreground p-5 shadow-xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-md",
        className
      )}
      {...props}>
      <div onPointerDown={geo.onMoveDown} data-testid="dialog-drag-bar" title="Drag to move" className={cn("absolute left-0 right-24 top-0 h-12 z-10", geo.maxed ? "" : "cursor-move")} />
      {children}
      <button type="button" onClick={geo.toggleMax} data-testid="dialog-maximize-toggle" title={geo.maxed ? "Restore" : "Maximize"} className={cn(btn, "right-11 z-20")}>
        {geo.maxed ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        <span className="sr-only">{geo.maxed ? "Restore" : "Maximize"}</span>
      </button>
      <DialogPrimitive.Close
        className={cn(btn, "right-4 z-20 data-[state=open]:bg-accent data-[state=open]:text-muted-foreground")}>
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
      {!geo.maxed && <div
        onPointerDown={geo.onResizeDown}
        title="Drag to resize"
        data-testid="dialog-resize-handle"
        className="absolute right-1 bottom-1 h-4 w-4 cursor-nwse-resize select-none touch-none opacity-50 hover:opacity-100 z-10"
        style={{ backgroundImage: "linear-gradient(135deg, transparent 50%, currentColor 50%, currentColor 60%, transparent 60%, transparent 75%, currentColor 75%, currentColor 85%, transparent 85%)" }} />}
    </DialogPrimitive.Content>
  </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end pt-1", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-[15px] font-bold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-[12px] text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
